<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="disabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" data-cy="dialog-uploadFiles-activator" />
    </template>

    <v-card data-cy="dialog-uploadFiles-modal">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog-uploadFiles-title">
        <span>{{ getDialogTitle }}</span>
        <v-btn @click="dialog = false" :aria-label="$t('common.close')" icon="mdi-close" />
      </v-card-title>

      <!-- Tabs for file upload configuration -->
      <v-tabs v-if="!pod" v-model="activeTab">
        <v-tab value="uploadFiles">
          <template v-slot:prepend>
            <v-icon>mdi-upload-box-outline</v-icon>
          </template>
          {{ $t('menus.uploadFiles.uploadFiles') }}
        </v-tab>
        <v-tab value="labels" v-if="willUploadFiles && $canLabelPods()">
          <template v-slot:prepend>
            <v-icon>mdi-label</v-icon>
          </template>
          {{ $t('menus.uploadFiles.labels') }} ({{ selectedLabelsCount }})
        </v-tab>
        <v-tab value="options" v-if="willUploadFiles">
          <template v-slot:prepend>
            <v-icon>mdi-cog</v-icon>
          </template>
          {{ $t('menus.uploadFiles.options') }} ({{ selectedOptionsCount }})
        </v-tab>
      </v-tabs>

      <!-- Duplicate file warning -->
      <div v-if="duplicateWarning" class="d-flex align-center mx-2 mt-2 px-3 warning-message rounded text-pre-wrap">
        <v-icon class="mr-2">mdi-alert</v-icon>
        <span class="text-body-1">{{ duplicateWarning }}</span>
        <v-spacer />
        <v-btn
          @click="duplicateWarning = null"
          icon="mdi-close-circle"
          size="x-small"
          variant="text"
          density="compact"
          :aria-label="$t('common.close')"
        />
      </div>

      <v-card-text class="scrollable mt-4">
        <v-window v-model="activeTab">
          <!-- Main upload tab -->
          <v-window-item
            value="uploadFiles"
            @dragover.prevent="dragOver"
            @dragenter.prevent="dragEnter"
            @dragleave.prevent="dragLeave"
            @drop.stop.prevent="droppedFiles"
            class="position-relative w-100"
          >
            <!-- Drag and drop zone overlay -->
            <div v-if="isDragging" class="upload-list-drop-zone d-flex flex-column rounded-lg">
              <div class="flex-grow-0 text-right w-100">
                <v-btn variant="text" size="small" @click="closeDragZone" icon="mdi-close" />
              </div>
              <div class="flex-grow-1 d-flex flex-column justify-center align-center">
                <div class="upload-list-drop-zone-icon">
                  <v-icon size="48" color="primary" opacity="0.7">mdi-image-multiple-outline</v-icon>
                </div>
                <div class="upload-list-drop-zone-label">{{ $t('routes.node.dropFilesHere') }}</div>
              </div>
            </div>

            <!-- File list -->
            <div>
              <v-list class="text-left upload-list rounded-lg" density="compact">
                <!-- Completed files group -->
                <template v-if="completedFiles.length > 0">
                  <v-list-item
                    @click="completedFilesExpanded = !completedFilesExpanded"
                    :aria-expanded="completedFilesExpanded ? 'true' : 'false'"
                    aria-controls="completed-files-group"
                  >
                    <template v-slot:prepend>
                      <v-icon>{{ completedFilesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </template>

                    <template v-slot:append />

                    <v-list-item-title>
                      {{ $t('menus.uploadFiles.NFilesUploaded', completedFiles.length) }}
                    </v-list-item-title>
                  </v-list-item>

                  <v-expand-transition>
                    <div id="completed-files-group" v-show="completedFilesExpanded">
                      <v-list-item
                        v-for="item in completedFiles"
                        :key="item.meta.fileuuid"
                        :ripple="false"
                        rounded="lg"
                        style="margin-left: 40px"
                        class="upload-list-item rounded-lg"
                      >
                        <v-list-item-title style="white-space: normal;">
                          <span v-if="!pod">
                            {{ truncateString(item.meta.nodename, { maxLength: 20, startOffset: 5, endOffset: 5 }) }} /
                            <span v-if="hasRelativePath(item)">
                              <span
                                v-for="(part, index) in getPathSegments(item)"
                                :key="index"
                              >
                                <span v-if="index < getPathSegments(item).length - 1">
                                  {{ part }} /
                                </span>
                              </span>
                            </span>
                          </span>
                          <v-tooltip location="bottom" style="z-index: 1200;"
                                     :aria-label="getFilenameTooltip(item)">
                            <template v-slot:activator="{ props }">
                              <span
                                v-bind="props"
                                class="word-wrap"
                                style="white-space: normal; word-break: break-word;"
                              >
                                {{ truncateString(item.meta.filename, { maxLength: 30, startOffset: 10, endOffset: 15 }) }}
                              </span>
                            </template>
                            <span>{{ getFilenameTooltip(item) }}</span>
                          </v-tooltip>
                        </v-list-item-title>

                        <v-list-item-subtitle class="upload-list-item_subtitle">
                          <div class="d-flex justify-space-between">
                            <div
                              :class="{'inline-message--error': item.status === STATUSES.ERROR}"
                              class="d-flex align-center upload-list-item_subtitle"
                            >
                              {{ getItemSubtitle(item) }}
                            </div>
                          </div>
                        </v-list-item-subtitle>

                        <template v-slot:append>
                          <v-btn
                            @click="removeSelectedFile(item)"
                            variant="text"
                            size="32"
                            icon="mdi-close"
                            color="primary"
                            :aria-label="$t('menus.uploadFiles.removeFile')"
                          />
                        </template>
                      </v-list-item>
                    </div>
                  </v-expand-transition>
                </template>

                <!-- Files pending upload -->
                <v-list-item
                  v-for="item in notCompletedFiles"
                  :key="item.meta.fileuuid"
                  :ripple="false"
                  rounded="lg"
                  class="upload-list-item rounded-lg"
                >
                  <v-list-item-title style="white-space: normal;">
                    <span v-if="!pod">
                      {{ truncateString(item.meta.nodename, { maxLength: 20, startOffset: 5, endOffset: 5 }) }} /
                      <span v-if="hasRelativePath(item)">
                        <span
                          v-for="(part, index) in getPathSegments(item)"
                          :key="index"
                        >
                          <span v-if="index < getPathSegments(item).length - 1">
                            {{ truncateString(part, { maxLength: 20, startOffset: 5, endOffset: 5 }) }} /
                          </span>
                        </span>
                      </span>
                    </span>
                    <v-tooltip location="bottom" style="z-index: 1200;" :aria-label="getFilenameTooltip(item)">
                      <template v-slot:activator="{ props }">
                        <span
                          v-bind="props"
                          class="word-wrap"
                          style="white-space: normal; word-break: break-word;"
                        >
                          {{ truncateString(item.meta.filename, { maxLength: 30, startOffset: 10, endOffset: 15 }) }}
                        </span>
                      </template>
                      <span>{{ getFilenameTooltip(item) }}</span>
                    </v-tooltip>
                  </v-list-item-title>

                  <!-- Note checkbox -->
                  <v-list-item-subtitle class="upload-list-item_subtitle">
                    <div class="d-flex justify-space-between">
                      <div v-if="showingShowNotesCheckbox(item)">
                        <v-checkbox
                          v-if="showingShowNotesCheckbox(item)"
                          v-model="showNotes[item.meta.fileuuid]"
                          :label="$t('menus.uploadFiles.addNote')"
                          hide-details
                        />
                      </div>
                    </div>
                  </v-list-item-subtitle>

                  <!-- Upload progress -->
                  <v-list-item-subtitle
                    v-show="item.status === STATUSES.UPLOADING"
                    class="upload-list-item_subtitle"
                  >
                    <v-progress-linear
                      :model-value="item.progress.percentage"
                      class="my-1"
                      color="primary"
                    />
                  </v-list-item-subtitle>

                  <!-- Status message -->
                  <v-list-item-subtitle class="upload-list-item_subtitle">
                    <div class="d-flex justify-space-between">
                      <div
                        :class="{'inline-message--error': item.status === STATUSES.ERROR}"
                        class="d-flex align-center upload-list-item_subtitle"
                      >
                        {{ getItemSubtitle(item) }}
                      </div>
                    </div>
                  </v-list-item-subtitle>

                  <!-- Legal category selection -->
                  <v-select
                    v-if="showSelectLegalCategory(item)"
                    class="mt-2"
                    v-model="legalCategory[item.meta.fileuuid]"
                    item-title="text"
                    item-value="value"
                    @update:model-value="setLegalCategory(item, $event)"
                    :error-messages="getLegalCategoryError(item)"
                    :items="legalCategoryOptions"
                    :label="$t('menus.uploadFiles.legalCategory')"
                    density="compact"
                    elevation="0"
                    variant="solo-filled"
                    flat
                    hide-details="auto"
                  />

                  <!-- Legal category custom name -->
                  <v-text-field
                    v-if="showSelectLegalCategory(item) && legalCategory[item.meta.fileuuid] === LEGAL_CATEGORIES.OTHER"
                    :label="$t('menus.uploadFiles.legalCategoryEnterName')"
                    :rules="legalCategoryNameRules"
                    @blur="setLegalCategoryName(item, $event)"
                    class="mt-2"
                    density="compact"
                    variant="solo-filled"
                    elevation="0"
                    hide-details="auto"
                    flat
                  />

                  <!-- Legal category expiry date -->
                  <div v-if="showSelectLegalCategory(item)">
                    <v-menu
                      v-model="datepickerMenu[item.meta.fileuuid]"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      location="bottom end"
                      min-width="auto"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          v-bind="props"
                          :model-value="getExpiryDate(item)"
                          :placeholder="$t('menus.uploadFiles.legalCategoryExpiryDate')"
                          :aria-label="$t('menus.uploadFiles.legalCategoryExpiryDate')"
                          :rules="expiryDateRules"
                          append-inner-icon="mdi-calendar"
                          density="compact"
                          readonly
                          required
                          block
                          variant="solo-filled"
                          flat
                          class="mt-2"
                        />
                      </template>
                      <v-date-picker
                        v-model="expiryDateValues[item.meta.fileuuid]"
                        @update:model-value="setLegalExpiryDate(item, $event)"
                        show-adjacent-months
                        rounded="md"
                      />
                    </v-menu>
                  </div>

                  <!-- Asset type selection -->
                  <v-select
                    v-if="showSelectAssetType(item)"
                    class="mt-2"
                    v-model="typesModel[item.meta.fileuuid]"
                    item-title="text"
                    item-value="value"
                    @update:model-value="setAssetTypes"
                    :items="assetTypeOptions"
                    :label="$t('menus.uploadFiles.assetType')"
                    density="compact"
                    variant="solo-filled"
                    hide-details
                    flat
                  />

                  <!-- Note textarea -->
                  <v-textarea
                    v-if="showingShowNotesCheckbox(item)"
                    v-show="showNotes[item.meta.fileuuid]"
                    @update:model-value="setNote(item, $event)"
                    :model-value="item.note"
                    :disabled="item.status !== STATUSES.ADDED"
                    variant="outlined"
                    density="comfortable"
                    rows="2"
                    :label="$t('common.note')"
                    class="mt-2"
                    hide-details
                    flat
                  />

                  <!-- Remove/Cancel button -->
                  <template v-slot:append>
                    <v-btn
                      @click="cancelOrCloseFileUpload(item)"
                      variant="text"
                      icon="mdi-close"
                      color="primary"
                      density="comfortable"
                      size="36"
                      :aria-label="$t('menus.uploadFiles.removeFile')"
                    />
                  </template>
                </v-list-item>

                <!-- Browse files action -->
                <v-list-item @click="browseFiles" class="mb-2">
                  <template v-slot:prepend>
                    <v-icon color="primary" class="mr-1">mdi-attachment</v-icon>
                  </template>
                  <v-list-item-title color="primary">
                    {{ $t('menus.uploadFiles.browseFiles') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="upload-list-item_subtitle">
                    {{ $t('menus.uploadFiles.clickToAddFiles') }}
                    <input
                      type="file"
                      ref="fileInput"
                      @change="filesSelected"
                      style="display: none"
                      multiple
                      :accept="getAcceptedExtensions"
                      data-cy="dialog-uploadFiles-inputFiles"
                    />
                  </v-list-item-subtitle>
                </v-list-item>

                <!-- Browse folders action -->
                <v-list-item @click="browseFolders" class="mb-2">
                  <template v-slot:prepend>
                    <v-icon color="primary" class="mr-1">mdi-attachment</v-icon>
                  </template>
                  <v-list-item-title color="primary">
                    {{ $t('menus.uploadFiles.browseFolders') }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="upload-list-item_subtitle" color="primary">
                    {{ $t('menus.uploadFiles.clickToAddFolders') }}
                    <input
                      type="file"
                      ref="foldersInput"
                      @change="filesSelected"
                      style="display: none"
                      multiple webkitdirectory
                      data-cy="dialog-uploadFiles-inputFolders"
                    />
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </div>

            <!-- Upload tips link -->
            <div class="mt-3">
              <a href="#" class="font-italic" @click.prevent="uploadTooltipsDialog = true">
                {{ $t('menus.uploadFiles.tipsForUploadingFilesTitle') }}
              </a>
              <UploadingFilesTipsDialog :is-visible="uploadTooltipsDialog" @close="uploadTooltipsDialog = false" />
            </div>
          </v-window-item>

          <!-- Labels tab -->
          <v-window-item v-if="willUploadFiles && $canLabelPods()" value="labels">
            <UploadDialogCustomLabels @labelsChanged="updateSelectedLabels" />
          </v-window-item>

          <!-- Options tab -->
          <v-window-item v-if="willUploadFiles" value="options">
            <div class="pa-6">
              <!-- Team-only Access Section -->
              <div v-if="showItemTeamonlyCheckbox" class="mb-6">
                <v-checkbox
                  v-model="teamonlyForAll"
                  :label="$t('routes.node.teamonlyAccess')"
                  hide-details
                  color="primary"
                />
                <div class="ml-8 mt-2">
                  <div class="text-medium-emphasis d-flex align-start">
                    <v-icon size="16" class="mr-1 mt-0">mdi-information-outline</v-icon>
                    <span>{{ $t('menus.uploadFiles.teamonlyAccessInfo') }}</span>
                  </div>
                </div>
              </div>

              <!-- Processing Options Section -->
              <div v-show="isSuitableForTranscoding">
                <!-- Priority Processing -->
                <div class="mb-6" v-if="canUsePriorityProcessing">
                  <v-checkbox
                    v-model="priorityProcessing"
                    :label="$t('menus.uploadFiles.priorityProcessing')"
                    hide-details
                    color="primary"
                  />
                  <div class="ml-8 mt-2">
                    <div class="text-medium-emphasis d-flex align-start">
                      <v-icon size="16" class="mr-1 mt-0" color="primary">mdi-flash</v-icon>
                      <span>{{ $t('menus.uploadFiles.priorityProcessingInfo') }}</span>
                    </div>
                  </div>
                </div>

                <!-- Disable Preview Generation -->
                <div>
                  <v-checkbox
                    v-model="disablePreview"
                    :label="$t('menus.uploadFiles.disableGeneratingPreview')"
                    hide-details
                    color="primary"
                  />
                  <div class="ml-8 mt-2">
                    <div class="text-medium-emphasis d-flex align-start">
                      <v-icon size="16" class="mr-1 mt-0" color="warning">mdi-eye-off-outline</v-icon>
                      <span>{{ $t('menus.uploadFiles.disableGeneratingPreviewInfo') }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Authenticity Check Section -->
              <div v-if="$canRunPaidService()">
                <v-checkbox
                  v-model="checkAuthenticity"
                  :label="$t('menus.uploadFiles.checkAuthenticity')"
                  hide-details
                  color="primary"
                />
                <div class="ml-8 mt-2">
                  <div class="text-medium-emphasis d-flex align-start">
                    <v-icon size="16" class="mr-1 mt-0" color="primary">mdi-shield-check-outline</v-icon>
                    <span>{{ $t('menus.uploadFiles.checkAuthenticityInfo') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>

      <!-- Dialog actions -->
      <v-card-actions>
        <v-btn v-if="$isAdobeExtension()" variant="text" @click="showAPPresetDialog">
          {{$t('menus.uploadFiles.addSequence')}}
        </v-btn>

        <APPresetDialog
          v-if="$isAdobeExtension()"
          v-model="showingAPPresetDialog"
          :data="{useruuid: USER.userId, nodeid: currentNode.id.toString() }"
        />

        <v-spacer />

        <v-btn
          v-show="hasAddedFiles"
          @click="uploadAllFiles"
          color="primary"
          variant="flat"
          data-cy="dialog-uploadFiles-uploadAllButton"
        >
          {{ $t('menus.uploadFiles.uploadAll') }}
        </v-btn>

        <v-btn
          v-show="hasFilesForUpload"
          @click="cancelUploads"
          color="primary"
          variant="flat"
          data-cy="dialog-uploadFiles-cancelAllUploadsButton"
          :aria-label="$t('menus.uploadFiles.cancelUploads')"
        >
          {{ $t('menus.uploadFiles.cancelUploads') }}
        </v-btn>

        <v-btn variant="text" @click="dialog = false" data-cy="dialog-uploadFiles-closeButton">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useUploadStore } from '@/stores/UploadStore';
import APPresetDialog from '@/routes/nodes2/components/dialogs/APPresetDialog.vue';
import UploadDialogCustomLabels from './components/UploadDialogCustomLabels.vue';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import Helper from '../../../../components/helper.js';
import UploadingFilesTipsDialog from '@/components/dialogs/UploadingFilesTipsDialog.vue';
import { formatDate, formatSize, truncateString } from '@/components/formatters.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import MimetypeHelper from '@/components/MimetypeHelper.js';
import {
  ASSET_TYPES,
  UPLOAD_DIALOG_TYPES,
  LEGAL_CATEGORIES,
  MAX_LEGAL_CATEGORY_NAME_LENGTH,
} from '@/components/utils/upload-constants.js';
import { processDroppedItems, processFile, generateRequestId } from '@/components/utils/upload-file-utils.js';
import { filterFilesByExtension, validateLegalInformation } from '@/components/utils/upload-validators.js';

export default {
  name: 'UploadFilesDialog',
  mixins: [DialogActionKeys, DialogModel],
  components: {
    UploadingFilesTipsDialog,
    APPresetDialog,
    UploadDialogCustomLabels,
  },
  props: {
    /** Target node where files will be uploaded */
    node: {
      type: Object,
      required: true,
    },
    /** Optional pod for asset uploads */
    pod: {
      type: Object,
      required: false,
    },
    /** Type of upload dialog (assets, subtitles, legal) */
    uploadDialogType: {
      type: String,
      validator: (value) => Object.values(UPLOAD_DIALOG_TYPES).includes(value),
      required: false,
    },
    /** Whether the dialog is disabled */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** External drop event to trigger file handling */
    filesDroppedEvent: {
      required: false,
    },
  },
  data() {
    return {
      // Constants
      LEGAL_CATEGORIES,

      // UI state
      uploadTooltipsDialog: false,
      showingAPPresetDialog: false,
      activeTab: 'uploadFiles',
      isDragging: false,
      dragCounter: 0,
      duplicateWarning: null,
      completedFilesExpanded: true,

      // File metadata
      typesModel: {},           // Asset type selections
      showNotes: {},            // Show note textarea flags
      legalCategory: {},        // Legal category selections
      datepickerMenu: {},       // Date picker visibility
      expiryDateValues: {},     // Legal expiry dates

      // Upload options
      labels: [],
      disablePreview: false,
      priorityProcessing: false,
      checkAuthenticity: false,
      teamonlyForAll: false,

      // Validation rules
      legalCategoryNameRules: [
        (v) => !v || v.length < MAX_LEGAL_CATEGORY_NAME_LENGTH
          || this.$t('menus.uploadFiles.legalCategoryNameShorterThan'),
      ],
      expiryDateRules: [],
    };
  },
  computed: {
    /**
     * Gets the current node from the store
     * @returns {object} Current node object
     */
    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },

    /**
     * Gets the authenticated user
     * @returns {object} User object
     */
    USER() {
      const authStore = useAuthStore();
      return authStore.user;
    },

    /**
     * Gets upload statuses from the store
     * @returns {object} Status constants
     */
    STATUSES() {
      const uploadStore = useUploadStore();
      return uploadStore.statuses;
    },

    /**
     * Gets blocked file IDs from the upload store
     * @returns {Array<string>} Array of blocked pod IDs
     */
    blockedFileIds() {
      const uploadStore = useUploadStore();
      return uploadStore.blockedFiles || [];
    },

    /**
     * Filters files relevant to current upload context
     * @returns {Array} Filtered files
     */
    selectedFiles() {
      const uploadStore = useUploadStore();
      const storedFiles = uploadStore.files;
      const selectedFiles = [];

      for (const file of storedFiles) {
        // Filter by pod ID if uploading assets to a specific pod
        if (this.pod && file.podid && file.podid == this.pod.id) {
          selectedFiles.push(file);
          continue;
        }

        // Filter files not associated with a pod and in ADDED status
        if (!this.pod && !file.podid && file.status == this.STATUSES.ADDED) {
          selectedFiles.push(file);
          continue;
        }

        // Include all files for the current node
        if (!this.pod && !file.podid && this.node) {
          selectedFiles.push(file);
        }
      }

      return selectedFiles;
    },

    /**
     * Gets completed files sorted by path
     * @returns {Array} Completed files
     */
    completedFiles() {
      return this.selectedFiles
        .filter((item) => item.status === this.STATUSES.FINISHED)
        .sort((a, b) => {
          const pathA = a.meta?.webkitRelativePath || '';
          const pathB = b.meta?.webkitRelativePath || '';
          return pathA.localeCompare(pathB);
        });
    },

    /**
     * Gets files that haven't completed upload
     * @returns {Array} Non-completed files
     */
    notCompletedFiles() {
      return this.selectedFiles
        .filter((item) => item.status !== this.STATUSES.FINISHED)
        .sort((a, b) => {
          const pathA = a.meta?.webkitRelativePath || '';
          const pathB = b.meta?.webkitRelativePath || '';
          return pathA.localeCompare(pathB);
        });
    },

    /**
     * Checks if there are files in ADDED status ready to upload
     * @returns {boolean}
     */
    hasAddedFiles() {
      return this.selectedFiles.some((file) => file.status === this.STATUSES.ADDED);
    },

    /**
     * Checks if there are files currently being uploaded
     * @returns {boolean}
     */
    hasFilesForUpload() {
      return this.selectedFiles.some((item) =>
        item.status === this.STATUSES.WAITING || item.status === this.STATUSES.UPLOADING
      );
    },

    /**
     * Determines if team-only checkbox should be shown
     * @returns {boolean}
     */
    showItemTeamonlyCheckbox() {
      return !this.pod && this.$canBelongstoTeam();
    },

    /**
     * Checks if files are suitable for transcoding (video/audio)
     * @returns {boolean}
     */
    isSuitableForTranscoding() {
      return this.selectedFiles.some((file) => {
        if (file?.file?.type) {
          return Helper.isVideo(file.file.type) || Helper.isAudio(file.file.type);
        }
        return false;
      });
    },

    /**
     * Determines if user can use priority processing
     * @returns {boolean}
     */
    canUsePriorityProcessing() {
      return this.$canRunPaidService();
    },

    /**
     * Determines if uploading to root (not to a pod)
     * @returns {boolean}
     */
    willUploadFiles() {
      return !this?.pod?.id;
    },

    /**
     * Counts selected labels
     * @returns {number}
     */
    selectedLabelsCount() {
      return this.labels.length;
    },

    /**
     * Counts selected options (teamonly, priority, disable preview)
     * @returns {number}
     */
    selectedOptionsCount() {
      let count = 0;
      if (this.teamonlyForAll) count++;
      if (this.priorityProcessing) count++;
      if (this.disablePreview) count++;
      if (this.checkAuthenticity) count++;
      return count;
    },

    /**
     * Gets dialog title based on upload type
     * @returns {string}
     */
    getDialogTitle() {
      switch (this.uploadDialogType) {
        case UPLOAD_DIALOG_TYPES.SUBTITLES:
          return this.$t('routes.pod.menuSubtitles.uploadSubtitleMenuItem');
        case UPLOAD_DIALOG_TYPES.LEGAL:
          return this.$t('menus.uploadFiles.uploadDocuments');
        default:
          return this.$t('menus.uploadFiles.uploadFilesAndFolders');
      }
    },

    /**
     * Gets accepted file extensions for file input
     * @returns {string}
     */
    getAcceptedExtensions() {
      const extensionsMap = {
        [UPLOAD_DIALOG_TYPES.LEGAL]: 'application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword',
        [UPLOAD_DIALOG_TYPES.SUBTITLES]: '.srt, .vtt',
      };

      return extensionsMap[this.uploadDialogType] || '*';
    },

    /**
     * Gets asset type options for dropdown
     * @returns {Array}
     */
    assetTypeOptions() {
      const options = [
        { value: ASSET_TYPES.AUTO, text: this.$t('menus.uploadFiles.assetTypeAuto') },
        { value: ASSET_TYPES.THUMBNAIL, text: this.$t('menus.uploadFiles.assetTypeThumbnail') },
        { value: ASSET_TYPES.SUBTITLE, text: this.$t('menus.uploadFiles.assetTypeSubtitle') },
      ];

      if (this.isVideoUploadPod) {
        options.push({
          value: ASSET_TYPES.AUDIODESCRIPTION,
          text: this.$t('menus.uploadFiles.assetTypeAudioDescription'),
        });
      }

      return options;
    },

    /**
     * Checks whether the upload target is a video pod.
     * @returns {boolean}
     */
    isVideoUploadPod() {
      return this.pod && MimetypeHelper.isMimetypeVideo(this.pod);
    },

    /**
     * Gets legal category options for dropdown
     * @returns {Array}
     */
    legalCategoryOptions() {
      return [
        { value: LEGAL_CATEGORIES.QUIT_CLAIM, text: this.$t('menus.uploadFiles.legalCategoryQuitClaim') },
        { value: LEGAL_CATEGORIES.RIGHTS_AGREEMENT, text: this.$t('menus.uploadFiles.legalCategoryRightsAgreement') },
        { value: LEGAL_CATEGORIES.RELEASE_FORM, text: this.$t('menus.uploadFiles.legalCategoryReleaseForm') },
        { value: LEGAL_CATEGORIES.OTHER, text: this.$t('menus.uploadFiles.legalCategoryOther') },
      ];
    },

    /**
     * Checks if subdirectories need refresh after upload
     * @returns {boolean}
     */
    needsToRefreshSubdirectories() {
      if (this.pod || this.getViewPod) {
        return false;
      }

      return this.selectedFiles.some((file) => {
        return file?.file?.webkitRelativePath && file.file.webkitRelativePath.indexOf('/') >= 0;
      });
    },

    /**
     * Gets the viewing pod from store
     * @returns {object|null}
     */
    getViewPod() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
  },
  watch: {
    /**
     * Syncs dialog state with modelValue prop
     */
    modelValue(val) {
      this.dialogModel = val;
    },

    /**
     * Resets state when dialog opens/closes
     * Hides drag zone when closing
     */
    dialogModel(val) {
      if (val) {
        this.resetState();
      }
      this.isDragging = false;
    },

    /**
     * Handles external drop events
     */
    filesDroppedEvent(val) {
      if (val) {
        this.droppedFiles(val);
        this.dialog = true;
      }
    },

    /**
     * Resets processing options when files become unsuitable
     */
    isSuitableForTranscoding(val) {
      if (!val) {
        this.priorityProcessing = false;
        this.disablePreview = false;
      }
    },

    /**
     * Watches for blocked files and marks them as ERROR
     * Automatically updates file status when backend blocks them
     */
    blockedFileIds: {
      handler(newBlockedIds) {
        if (!newBlockedIds || newBlockedIds.length === 0) {
          return;
        }

        const uploadStore = useUploadStore();

        for (const file of this.selectedFiles) {
          // Skip files that are already finished or errored
          if (file.status === this.STATUSES.ERROR) {
            continue;
          }

          // Check if this file's podid is in the blocked list
          if (file.meta.podid && newBlockedIds.includes(file.meta.podid)) {
            uploadStore._setStatusError({
              file,
              errorMessage: 'menus.uploadFiles.fileBlockedNotAllowedType',
            });
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    // Import utility methods
    truncateString,
    generateRequestId,

    /**
     * Triggers file input click
     */
    browseFiles() {
      this.$refs.fileInput.click();
    },

    /**
     * Triggers folder input click
     */
    browseFolders() {
      this.$refs.foldersInput.click();
    },

    /**
     * Closes the drag and drop zone
     */
    closeDragZone() {
      this.isDragging = false;
      this.dragCounter = 0;
    },

    /**
     * Handles file selection from input
     * Processes files and adds them to upload queue
     * @param {Event} event - Change event from file input
     */
    async filesSelected(event) {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      // Process files to ensure MIME types
      const processedFiles = Array.from(event.target.files).map(processFile);

      // Filter by allowed extensions
      const allowedExtensions = this.getAllowedExtensions();
      const filteredFiles = filterFilesByExtension(processedFiles, allowedExtensions);

      if (filteredFiles.length === 0) {
        return;
      }

      // Add files to upload store
      await this.addFilesToStore(filteredFiles);

      // Clear input values
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
      if (this.$refs.foldersInput) {
        this.$refs.foldersInput.value = null;
      }
    },

    /**
     * Handles drag and drop file uploads
     * @param {DragEvent} event - Drop event
     */
    async droppedFiles(event) {
      this.isDragging = false;
      this.dragCounter = 0;

      if (!event.dataTransfer) {
        return;
      }

      // Process dropped items (files and directories)
      const filesList = await processDroppedItems(event.dataTransfer);

      if (filesList.length === 0) {
        return;
      }

      // Filter by allowed extensions
      const allowedExtensions = this.getAllowedExtensions();
      const filteredFiles = filterFilesByExtension(filesList, allowedExtensions);

      if (filteredFiles.length > 0) {
        await this.addFilesToStore(filteredFiles);
      }
    },

    /**
     * Adds files to the upload store
     * Handles duplicate detection and notifications
     * @param {File[]} files - Files to add
     */
    async addFilesToStore(files) {
      const payload = {
        nodeID: this.node.id,
        podID: this.pod ? this.pod.id : null,
        fileList: files,
      };

      const uploadStore = useUploadStore();
      const result = uploadStore.addFiles(payload);

      if (result?.duplicateFiles?.length > 0) {
        this.showDuplicateNotification(result.duplicateFiles);
      }
    },

    /**
     * Shows notification for duplicate files
     * @param {string[]} duplicateFiles - Array of duplicate filenames
     */
    showDuplicateNotification(duplicateFiles) {
      const message = duplicateFiles.length === 1
        ? this.$t('menus.uploadFiles.duplicateFile', { filename: duplicateFiles[0] })
        : this.$t('menus.uploadFiles.duplicateFiles', { count: duplicateFiles.length });

      this.duplicateWarning = message;
    },

    /**
     * Gets allowed extensions based on dialog type
     * @returns {string[]|null} Array of extensions or null for all files
     */
    getAllowedExtensions() {
      const extensionsMap = {
        [UPLOAD_DIALOG_TYPES.SUBTITLES]: ['srt', 'vtt'],
        [UPLOAD_DIALOG_TYPES.LEGAL]: ['pdf', 'docx', 'doc'],
      };

      return extensionsMap[this.uploadDialogType] || null;
    },

    /**
     * Drag and drop event handlers
     */
    dragOver(event) {
      event.preventDefault();
      event.stopPropagation();
    },

    dragEnter(event) {
      event.preventDefault();
      this.dragCounter++;
      if (event?.dataTransfer?.items?.length > 0) {
        this.isDragging = true;
      }
    },

    dragLeave(event) {
      event.preventDefault();
      this.dragCounter--;
      if (this.dragCounter === 0) {
        this.isDragging = false;
      }
    },

    /**
     * Uploads all files in the queue
     * Validates legal information before starting upload
     * Refreshes node if necessary after completion
     */
    async uploadAllFiles() {
      // Validate legal category selections
      const validation = validateLegalInformation(
        this.notCompletedFiles,
        this.uploadDialogType === UPLOAD_DIALOG_TYPES.LEGAL,
        this.legalCategory
      );

      if (!validation.isValid) {
        return;
      }

      // Prepare upload payload
      const payload = {
        labels: this.labels,
        priorityProcessing: this.canUsePriorityProcessing && this.priorityProcessing ? 1 : 0,
        teamonlyForAll: this.teamonlyForAll ? 1 : 0,
        disablePreview: this.disablePreview ? 1 : 0,
        checkAuthenticity: this.checkAuthenticity ? 1 : 0,
        requestId: generateRequestId(),
      };

      const uploadStore = useUploadStore();
      await uploadStore.uploadAllFiles(payload);

      // Refresh node if uploaded files contain subdirectories
      if (this.node.id === this.currentNode.id && this.needsToRefreshSubdirectories) {
        const nodeStore = useNodeStore();
        await nodeStore.viewNode(this.node.id);
      }
    },

    /**
     * Cancels all ongoing uploads
     */
    async cancelUploads() {
      const uploadStore = useUploadStore();
      await uploadStore.cancelUploads();
    },

    /**
     * Cancels or removes a file from upload queue
     * @param {object} item - File item to cancel/remove
     */
    async cancelOrCloseFileUpload(item) {
      if (item.status === this.STATUSES.WAITING || item.status === this.STATUSES.UPLOADING) {
        const uploadStore = useUploadStore();
        await uploadStore.cancelUpload(item);
        return;
      }

      this.removeSelectedFile(item);
    },

    /**
     * Removes a file from the upload list
     * @param {object} item - File item to remove
     */
    removeSelectedFile(item) {
      const uploadStore = useUploadStore();
      uploadStore.removeFile(item);
    },

    /**
     * Sets asset types for all files
     * Updates store with selected types
     */
    setAssetTypes() {
      for (const prop in this.typesModel) {
        if (Object.prototype.hasOwnProperty.call(this.typesModel, prop)) {
          const payload = {
            fileuuid: prop,
            value: this.typesModel[prop],
          };

          const uploadStore = useUploadStore();
          uploadStore.setAssetType(payload);
        }
      }
    },

    /**
     * Sets legal category for a file
     * @param {object} item - File item
     * @param {number} categoryId - Legal category ID
     */
    setLegalCategory(item, categoryId) {
      const uploadStore = useUploadStore();
      uploadStore.setLegalCategoryId(item.meta.fileuuid, categoryId);
    },

    /**
     * Sets legal category custom name
     * @param {object} item - File item
     * @param {Event} event - Blur event
     */
    setLegalCategoryName(item, event) {
      const categoryName = event.target.value;
      const uploadStore = useUploadStore();
      uploadStore.setLegalCategoryName(item.meta.fileuuid, categoryName);
    },

    /**
     * Sets legal document expiry date
     * @param {object} item - File item
     * @param {Date} value - Selected date
     */
    setLegalExpiryDate(item, value) {
      this.datepickerMenu[item.meta.fileuuid] = false;
      this.expiryDateValues[item.meta.fileuuid] = value;

      const uploadStore = useUploadStore();
      uploadStore.setLegalExpiryDate(item.meta.fileuuid, value);
    },

    /**
     * Sets note for a file
     * @param {object} item - File item
     * @param {string} note - Note text
     */
    setNote(item, note) {
      const payload = {
        file: item,
        note,
      };

      const uploadStore = useUploadStore();
      uploadStore.setNote(payload);
    },

    /**
     * Updates selected labels
     * @param {Array} labels - Selected labels
     */
    updateSelectedLabels(labels) {
      this.labels = labels;
    },

    /**
     * Resets component state
     */
    resetState() {
      this.labels = [];
      this.dragCounter = 0;
      this.isDragging = false;
      this.duplicateWarning = null;
    },

    /**
     * Computed methods for template
     */

    /**
     * Checks if file has relative path
     * @param {object} item - File item
     * @returns {boolean}
     */
    hasRelativePath(item) {
      return item.meta.webkitRelativePath && item.meta.webkitRelativePath.split('/').length > 1;
    },

    /**
     * Gets path segments for display
     * @param {object} item - File item
     * @returns {string[]}
     */
    getPathSegments(item) {
      return item.meta.webkitRelativePath.split('/');
    },

    /**
     * Gets filename tooltip text
     * @param {object} item - File item
     * @returns {string}
     */
    getFilenameTooltip(item) {
      return item.meta.webkitRelativePath || item.meta.filename;
    },

    /**
     * Gets item subtitle based on status
     * @param {object} item - File item
     * @returns {string}
     */
    getItemSubtitle(item) {
      switch (item.status) {
        case this.STATUSES.ADDED:
          return `(${formatSize(item.meta.filesize, 0)})`;
        case this.STATUSES.WAITING:
          return this.$t('menus.uploadFiles.statusWaiting');
        case this.STATUSES.UPLOADING:
          return this.$t('menus.uploadFiles.statusUploading', [
            formatSize(item.progress.uploaded, 1),
            formatSize(item.meta.filesize, 1)
          ]);
        case this.STATUSES.FINISHED:
          return this.$t('menus.uploadFiles.statusFinished');
        case this.STATUSES.ERROR: {
          let text = this.$t('menus.uploadFiles.statusError');

          if (item.errorMessage) {
            text = this.$t(item.errorMessage, item.errorMessageParameters);
          }

          return text;
        }
        case this.STATUSES.CANCELLED:
          return this.$t('menus.uploadFiles.statusCancelled');
        default:
          return this.$t('menus.uploadFiles.statusUnknown');
      }
    },

    /**
     * Determines if note checkbox should be shown
     * @param {object} item - File item
     * @returns {boolean}
     */
    showingShowNotesCheckbox(item) {
      return !this.pod && item.status == this.STATUSES.ADDED;
    },

    /**
     * Determines if asset type selection should be shown
     * @param {object} item - File item
     * @returns {boolean}
     */
    showSelectAssetType(item) {
      return this.uploadDialogType === UPLOAD_DIALOG_TYPES.ASSETS
        && this.pod
        && item.status === this.STATUSES.ADDED;
    },

    /**
     * Determines if legal category selection should be shown
     * @param {object} item - File item
     * @returns {boolean}
     */
    showSelectLegalCategory(item) {
      return this.uploadDialogType === UPLOAD_DIALOG_TYPES.LEGAL
        && this.pod
        && item.status === this.STATUSES.ADDED;
    },

    /**
     * Gets legal category validation error
     * @param {object} item - File item
     * @returns {string|undefined}
     */
    getLegalCategoryError(item) {
      const fileuuid = item.meta.fileuuid;
      if (!this.legalCategory[fileuuid]) {
        return this.$t('menus.uploadFiles.pleaseSelectLegalCategory');
      }
    },

    /**
     * Gets formatted expiry date
     * @param {object} item - File item
     * @returns {string}
     */
    getExpiryDate(item) {
      const fileuuid = item.meta.fileuuid;
      return this.expiryDateValues[fileuuid] ? formatDate(this.expiryDateValues[fileuuid]) : '';
    },

    showAPPresetDialog() {
      this.showingAPPresetDialog = true;
    },
  },
};
</script>

<style scoped>
.warning-message {
  background-color: #ef964f;
  border: 1px solid rgba(var(--v-theme-warning), 0.3);
}
</style>
